import TelehealthSettings from '../../components/telehealth/TelehealthSettings';
import HospitalarSettings from '../../components/hospitalar/HospitalarSettings';
import AdminUnitCapacityModal from './AdminUnitCapacityModal';
import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Badge, Spinner, Alert, Modal, Row, Col, Form } from 'react-bootstrap';
import axios from 'axios';
import { useAuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

import AdminCampaignLimitsModal from './AdminCampaignLimitsModal';
import AdminConnectionGrantsModal from './AdminConnectionGrantsModal';

const AdminEntidadesPage = () => {
  const [unitEntity, setUnitEntity] = useState(null);
  const [telehealthEntity, setTelehealthEntity] = useState(null);
  const [hospitalarEntity, setHospitalarEntity] = useState(null);
  const [campaignEntity, setCampaignEntity] = useState(null);
  const [connectionEntity, setConnectionEntity] = useState(null);
  const [entidades, setEntidades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonatingEntidadeId, setImpersonatingEntidadeId] = useState(null);
  const [accessEntity, setAccessEntity] = useState(null);
  const [accessReason, setAccessReason] = useState('');
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntidade, setSelectedEntidade] = useState(null);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [isSavingModules, setIsSavingModules] = useState(false);
  const [modulesError, setModulesError] = useState(null);

  const { startImpersonation } = useAuthContext();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchEntidades = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/api/admin/entidades');
        setEntidades(response.data.entidades || []);
        setError(null);
      } catch (err) {
        setError('Não foi possível carregar a lista de empresas.');
        console.error("Erro ao buscar entidades:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntidades();
  }, []);

  const handleImpersonate = async (entidade) => {
    setImpersonatingEntidadeId(entidade.id);
    try {
      const response = await axios.post(`/api/admin/entidades/${entidade.id}/impersonate`);
      await startImpersonation(response.data);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Erro ao tentar personificar a empresa.';
      alert(errorMessage);
      console.error(error);
      setImpersonatingEntidadeId(null);
    }
  };

  const handleShowDetails = (entidade) => {
    setSelectedEntidade(entidade);
    setShowDetailsModal(true);
  };

  const saveAccess = async () => {
    if (!accessEntity || accessSaving) return;
    setAccessSaving(true); setAccessError('');
    try {
      const response = await axios.put(`/api/admin/entidades/${accessEntity.id}/access`, { active: accessEntity.acessoAtivo === false, reason: accessReason });
      setEntidades(current => current.map(item => item.id === accessEntity.id ? { ...item, ...response.data } : item));
      setAccessEntity(null);
    } catch (err) { setAccessError(err.response?.data?.message || 'Não foi possível alterar o acesso.'); }
    finally { setAccessSaving(false); }
  };

  const handleShowModules = async (entidade) => {
    setSelectedEntidade(entidade);
    setShowModulesModal(true);
    setIsLoadingModules(true);
    setModulesError(null);

    try {
      const response = await axios.get(`/api/admin/entidades/${entidade.id}/modules`);
      setModuleCatalog(response.data.catalog || []);
      setSelectedModules(response.data.active_modules || []);
    } catch (err) {
      setModulesError(err.response?.data?.message || 'Não foi possível carregar os módulos da empresa.');
    } finally {
      setIsLoadingModules(false);
    }
  };

  const handleToggleModule = (module) => {
    if (module.required) return;

    setSelectedModules(current => {
      if (current.includes(module.key)) {
        const dependentKeys = moduleCatalog
          .filter(item => item.depends_on?.includes(module.key))
          .map(item => item.key);

        return current.filter(key => key !== module.key && !dependentKeys.includes(key));
      }

      return [...new Set([...current, module.key, ...(module.depends_on || [])])];
    });
  };

  const handleSaveModules = async () => {
    if (!selectedEntidade) return;

    setIsSavingModules(true);
    setModulesError(null);

    try {
      const response = await axios.put(
        `/api/admin/entidades/${selectedEntidade.id}/modules`,
        { modules: selectedModules }
      );
      const activeModules = response.data.active_modules || [];

      setSelectedModules(activeModules);
      setEntidades(current => current.map(entidade => entidade.id === selectedEntidade.id
        ? { ...entidade, modulosAtivos: activeModules }
        : entidade));
      setShowModulesModal(false);
    } catch (err) {
      setModulesError(err.response?.data?.message || 'Não foi possível salvar os módulos da empresa.');
    } finally {
      setIsSavingModules(false);
    }
  };

  const groupedModules = moduleCatalog.reduce((groups, module) => {
    const group = module.group || 'Outros';
    groups[group] = [...(groups[group] || []), module];
    return groups;
  }, {});

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Ativa': return 'success';
      case 'Inadimplente': return 'warning';
      case 'Cancelada': return 'danger';
      case 'Trial': return 'info';
      case 'Inativa': return 'secondary';
      case 'Inativo': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <Container fluid>
      <h1 className="mb-4">Gerenciamento de Empresas</h1>
      <Card>
        <Card.Header>
          Lista de Empresas Cadastradas
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#ID</th>
                  <th>Nome da Empresa</th>
                  <th>CNPJ</th>
                  <th className="text-center">Status da Assinatura</th>
                  <th className="text-center">Status</th>
                  <th>Data de Cadastro</th>
                  <th title="Última entrada autenticada de um usuário da instituição. Não inclui personificação pelo administrador.">Último login</th>
                  <th className="text-center">Corpo Clínico</th>
                  <th className="text-center">Pacientes Ativos</th>
                  <th className="text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {entidades.map(entidade => (
                  <tr key={entidade.id}>
                    <td>{entidade.id}</td>
                    <td>{entidade.nome}</td>
                    <td>{entidade.cnpj}</td>
                    <td className="text-center">
                      <Badge bg={getStatusBadge(entidade.statusAssinatura)}>
                        {entidade.statusAssinatura}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <Badge pill bg={getStatusBadge(entidade.status)} style={{ fontSize: '0.9em' }}>
                        {entidade.status || 'Não informado'}
                      </Badge>
                    </td>
                    <td>{new Date(entidade.dataCadastro + 'T00:00:00').toLocaleDateString('pt-BR')}</td>
                    <td>{entidade.ultimoLogin
                      ? <time dateTime={entidade.ultimoLogin}>{new Date(entidade.ultimoLogin).toLocaleString('pt-BR')}</time>
                      : <span className="text-muted" title="O histórico começa após a ativação deste registro; a ausência de data não significa que nunca houve acesso.">Sem registro</span>}</td>
                    <td className="text-center">{entidade.totalCorpoClinico}</td>
                    <td className="text-center">{entidade.totalPacientesAtivos}</td>
                    <td className="text-center">
                      <Button variant="outline-info" size="sm" className="me-2" title="Ver Detalhes" onClick={() => handleShowDetails(entidade)}>
                        Ver
                      </Button>
                      <Button variant={entidade.acessoAtivo === false ? 'outline-success' : 'outline-danger'} size="sm" className="me-2" onClick={() => { setAccessEntity(entidade); setAccessReason(''); setAccessError(''); }}>{entidade.acessoAtivo === false ? 'Reativar acesso' : 'Inativar acesso'}</Button>
                      <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => setUnitEntity(entidade)}>Unidades</Button>
                      <Button variant="outline-info" size="sm" className="me-2" onClick={() => setTelehealthEntity(entidade)}>Teleconsulta</Button>
                      <Button variant="outline-primary" size="sm" className="me-2" onClick={() => setHospitalarEntity(entidade)}>Hospitalar</Button>
                      <Button variant="outline-dark" size="sm" className="me-2" onClick={() => setCampaignEntity(entidade)}>Limites de campanhas</Button>
                      <Button variant="outline-success" size="sm" className="me-2" onClick={() => setConnectionEntity(entidade)}>Conexões</Button>
                      <Button variant="outline-primary" size="sm" className="me-2" title="Configurar módulos" onClick={() => handleShowModules(entidade)}>
                        Módulos
                      </Button>
                      <Button 
                        variant="outline-info" 
                        size="sm" 
                        title="Personificar (logar como Representante Legal desta clínica)"
                        onClick={() => handleImpersonate(entidade)}
                        disabled={impersonatingEntidadeId === entidade.id}
                      >
                        {impersonatingEntidadeId === entidade.id ? (
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        ) : (
                          'Personificar'
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!telehealthEntity} onHide={() => setTelehealthEntity(null)} centered><Modal.Header closeButton><Modal.Title>Teleconsulta</Modal.Title></Modal.Header><Modal.Body>{telehealthEntity && <TelehealthSettings key={telehealthEntity.id} entityId={telehealthEntity.id} />}</Modal.Body></Modal>
      <Modal show={!!hospitalarEntity} onHide={() => setHospitalarEntity(null)} centered size="lg"><Modal.Header closeButton><Modal.Title>Hospitalar — {hospitalarEntity?.nome}</Modal.Title></Modal.Header><Modal.Body>{hospitalarEntity && <HospitalarSettings key={hospitalarEntity.id} entityId={hospitalarEntity.id} />}</Modal.Body></Modal>
      {unitEntity && <AdminUnitCapacityModal key={unitEntity.id} entity={unitEntity} onClose={() => setUnitEntity(null)} />}
      {campaignEntity && <AdminCampaignLimitsModal key={campaignEntity.id} entity={campaignEntity} onClose={() => setCampaignEntity(null)} />}
      {connectionEntity && <AdminConnectionGrantsModal key={connectionEntity.id} entity={connectionEntity} onClose={() => setConnectionEntity(null)} />}

      {/* Modal de Detalhes da Empresa */}
      <Modal show={Boolean(accessEntity)} onHide={() => !accessSaving && setAccessEntity(null)}>
        <Modal.Header closeButton={!accessSaving}><Modal.Title>{accessEntity?.acessoAtivo === false ? 'Reativar acesso' : 'Inativar acesso'} · {accessEntity?.nome}</Modal.Title></Modal.Header>
        <Modal.Body>
          {accessError && <Alert variant="danger">{accessError}</Alert>}
          {accessEntity?.acessoAtivo === false ? <p>A reativação exige uma assinatura ativa ou vitalícia. O acesso continua sujeito às permissões dos perfis.</p> : <>
            <Alert variant="warning">Todos os profissionais perderão acesso às funcionalidades, inclusive nas sessões abertas. Somente o Representante Legal poderá entrar em Plano e Assinatura para regularizar.</Alert>
            <Form.Group><Form.Label htmlFor="access-reason">Motivo da inativação</Form.Label><Form.Control id="access-reason" as="textarea" maxLength={500} value={accessReason} onChange={e => setAccessReason(e.target.value)} /></Form.Group>
          </>}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" disabled={accessSaving} onClick={() => setAccessEntity(null)}>Cancelar</Button><Button variant={accessEntity?.acessoAtivo === false ? 'success' : 'danger'} disabled={accessSaving || (accessEntity?.acessoAtivo !== false && !accessReason.trim())} onClick={saveAccess}>{accessSaving ? 'Salvando…' : 'Confirmar'}</Button></Modal.Footer>
      </Modal>
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes da Empresa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntidade && (
            <Row>
              <Col md={6}>
                <p><strong>ID:</strong> {selectedEntidade.id}</p>
                <p><strong>Nome:</strong> {selectedEntidade.nome}</p>
                <p><strong>CNPJ:</strong> {selectedEntidade.cnpj}</p>
                <p><strong>Data de Cadastro:</strong> {new Date(selectedEntidade.dataCadastro + 'T00:00:00').toLocaleDateString('pt-BR')}</p>
                <p><strong>Último login:</strong> {selectedEntidade.ultimoLogin ? new Date(selectedEntidade.ultimoLogin).toLocaleString('pt-BR') : 'Sem registro'}</p>
                <small className="text-muted">Última entrada autenticada de alguém da instituição. Não mede atividade de sessões abertas nem inclui personificação pelo SuperAdmin. O histórico começa após a ativação do registro.</small>
              </Col>
              <Col md={6}>
                <p><strong>Status da Empresa:</strong> <Badge bg={getStatusBadge(selectedEntidade.status)}>{selectedEntidade.status}</Badge></p>
                <p><strong>Status da Assinatura:</strong> <Badge bg={getStatusBadge(selectedEntidade.statusAssinatura)}>{selectedEntidade.statusAssinatura}</Badge></p>
                <p><strong>Total Corpo Clínico:</strong> {selectedEntidade.totalCorpoClinico}</p>
                <p><strong>Total Pacientes Ativos:</strong> {selectedEntidade.totalPacientesAtivos}</p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Fechar
          </Button>
          <Button variant="primary" onClick={() => alert('Função de edição a ser implementada.')}>
            Editar Clínica
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showModulesModal} onHide={() => !isSavingModules && setShowModulesModal(false)} size="lg">
        <Modal.Header closeButton={!isSavingModules}>
          <Modal.Title>Módulos da empresa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEntidade && (
            <p className="text-muted mb-3">
              Configure os recursos disponíveis para <strong>{selectedEntidade.nome}</strong>.
              As permissões dos funcionários continuam sendo controladas pelos perfis de acesso.
            </p>
          )}

          {modulesError && <Alert variant="danger">{modulesError}</Alert>}

          {isLoadingModules ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            Object.entries(groupedModules).map(([group, modules]) => (
              <Card key={group} className="mb-3">
                <Card.Header className="fw-semibold">{group}</Card.Header>
                <Card.Body className="py-2">
                  <Row>
                    {modules.map(module => (
                      <Col md={6} key={module.key} className="py-2">
                        <Form.Check
                          type="switch"
                          id={`entity-module-${module.key}`}
                          label={module.label}
                          checked={module.required || selectedModules.includes(module.key)}
                          disabled={module.required || isSavingModules}
                          onChange={() => handleToggleModule(module)}
                        />
                        {module.required && (
                          <small className="text-muted">Módulo essencial</small>
                        )}
                        {!module.required && module.depends_on?.length > 0 && (
                          <small className="text-muted d-block">
                            Inclui: {module.depends_on
                              .map(key => moduleCatalog.find(item => item.key === key)?.label || key)
                              .join(' e ')}
                          </small>
                        )}
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={isSavingModules} onClick={() => setShowModulesModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={isLoadingModules || isSavingModules || Boolean(modulesError)} onClick={handleSaveModules}>
            {isSavingModules ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Salvando...</>
            ) : 'Salvar módulos'}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default AdminEntidadesPage;
